import type { AuthUser } from '@fluxus/shared';
import { LogOut, MonitorSmartphone } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api/errors';

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Usuário logado: nome, perfil e empresa, e a ação de sair. */
export function UserMenu({ user, onLogout }: { user: AuthUser; onLogout: () => Promise<void> }) {
  const [leaving, setLeaving] = useState(false);

  // Se o servidor não confirmar a saída, a sessão continua (e o usuário fica sabendo).
  const leave = async () => {
    setLeaving(true);
    try {
      await onLogout();
    } catch (err) {
      toast.error('Não foi possível sair agora', {
        description: `${errorMessage(err)} Sua sessão continua aberta.`,
        action: { label: 'Tentar novamente', onClick: () => void leave() },
      });
    } finally {
      setLeaving(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-10 gap-2 px-1.5 md:px-2" aria-label={`Conta de ${user.name}`}>
          {/* Iniciais só de enfeite: no conteúdo do CSS, fora do texto do botão (o nome acessível contém o texto
              visível — WCAG 2.5.3). */}
          <span
            aria-hidden
            data-initials={initials(user.name)}
            className="inline-flex size-8 items-center justify-center rounded-full bg-brand-soft text-xs font-bold text-brand-strong after:content-[attr(data-initials)] dark:text-brand"
          />
          <span className="hidden max-w-40 truncate text-sm font-semibold md:inline">{user.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2 font-normal">
          <span className="truncate text-sm font-bold text-foreground">{user.name}</span>
          <span className="truncate">{user.email}</span>
          <span className="truncate">
            {user.role.name} · {user.organization.name}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Minha conta</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/configuracoes/minha-conta/sessoes">
            <MonitorSmartphone aria-hidden />
            Sessões ativas
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={leaving} onSelect={() => void leave()}>
          <LogOut aria-hidden />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
